import {
  Ranking,
  RankingStrategyTypes,
  SyteProductTypes,
  merchandisingRulesService,
} from 'src/services';
import { SyteProductType } from 'src/services/src/service/types/constants/syte-product-types';
import { VariantFeature } from 'src/components-bl/Experiment/ExperimentForm/VariantPanel/constants';
import { getViewConfigAdapter } from './ViewConfigEditor/viewConfigAdapters';
import { fetchAllMerchRules } from './fetchAllMerchRules';

const rankingService = new Ranking();

export interface ResetLeverToControlArgs {
  shopId: number;
  feature: VariantFeature;
  surface: SyteProductType;
  controlVariantId: string;
  testVariantId: string;
}

// Every editor writes straight to its own persistent per-surface document on the Test variant,
// independent of which lever the wizard currently has "selected" - so switching your What-to-test
// choice never cleaned up the previous lever's drift on its own. Called from
// `useWhatToTestStep.onSubmit` right before a lever change is saved, so the Test variant only ever
// carries changes for the one currently-active lever.
async function resetRankingStrategy({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
}: ResetLeverToControlArgs): Promise<void> {
  const syteProduct = surface as unknown as SyteProductTypes.SyteProductType;
  const control = await rankingService.get({ shopId, syteProduct, variantId: controlVariantId });
  await rankingService.update({
    shopId,
    syteProduct,
    variantId: testVariantId,
    weights: control.weights as RankingStrategyTypes.RankingStrategyWeight[],
    type: control.type,
  });
}

async function resetViewConfig({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
}: ResetLeverToControlArgs): Promise<void> {
  const adapter = getViewConfigAdapter(surface);
  if (!adapter) return;
  const control = await adapter.get({ shopId, variantId: controlVariantId });
  await adapter.update({ shopId, variantId: testVariantId, value: control });
}

async function resetMerchRules({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
}: ResetLeverToControlArgs): Promise<void> {
  const [controlRules, testRules] = await Promise.all([
    fetchAllMerchRules({ shopId, variantId: controlVariantId, product: [surface] }),
    fetchAllMerchRules({ shopId, variantId: testVariantId, product: [surface] }),
  ]);

  const testById = new Map(testRules.map(rule => [rule.id, rule]));

  const changedRules = controlRules
    .filter(control => {
      const test = testById.get(control.id);
      return (
        test &&
        (test.active !== control.active ||
          test.action !== control.action ||
          test.weight !== control.weight)
      );
    })
    .map(control => {
      const test = testById.get(control.id);
      return {
        id: test?.id ?? control.id,
        active: control.active,
        action: control.action,
        weight: control.weight,
      };
    });

  if (!changedRules.length) return;

  await merchandisingRulesService.bulkUpdate({
    shopId,
    variantId: testVariantId,
    rules: changedRules,
  });
}

export function resetLeverToControl(args: ResetLeverToControlArgs): Promise<void> {
  switch (args.feature) {
    case VariantFeature.RankingStrategy:
      return resetRankingStrategy(args);
    case VariantFeature.RecEnginesGeneralSettings:
      return resetViewConfig(args);
    case VariantFeature.MerchandisingRules:
      return resetMerchRules(args);
    default:
      return Promise.resolve();
  }
}
